import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CampaignSearch from '@components/CampaignSearch'

import type { CampaignModel } from '@models/types'

import useCreateCampaignPublicationOrderModal from './hooks/useCreateCampaignPublicationOrderModal'

import Campaign from './components/Campaign'

const MODAL_KEY = 'CreateCampaignPublicationOrderModal'

type ModalCallbacks = {
  closeModal: () => void,
  createOrder: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: object,
  showModal: boolean,
}

const CreateCampaignPublicationOrderModal = (props: ModalProps) => {
  const { callbacks, requestOptions, showModal } = props
  const { closeModal, createOrder } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    publicationEdition: initPublicationEdition,
  } = modalPayload

  const createCampaignPublicationOrderPayload = useCreateCampaignPublicationOrderModal({
    publicationEdition: initPublicationEdition,
    requestOptions,
  })

  const {
    callbacks: {
      createOrders: createFn,
      selectServiceCategoryId,
      selectServiceProductId,
      setState,
      setupOrderPayload,
    },
    campaignPayload: {
      campaign,
    },
    creatingOrder,
    filteredServiceProducts,
    saveEnabled,
    serviceCategoriesPayload: {
      filteredServiceCategories,
    },
    state: {
      searchString,
      selectedCampaignId,
      selectedServiceCategoryId,
      selectedServiceProductId,
    },
  } = createCampaignPublicationOrderPayload

  // To be used with handleCallbackAction
  const createOrderPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
      buildActionParams: setupOrderPayload,
    },
    toastText: 'Order Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Publication Order" />

      <SidebarModal.Content>
        <Form>
          {!selectedCampaignId && (
            <Form.Field label="* Search Campaigns">
              <CampaignSearch
                callbacks={{
                  onChange: value => setState({ searchString: value }),
                  selectItem: (item: CampaignModel) => setState({
                    searchString: '',
                    selectedCampaignId: item.id,
                  }),
                }}
                value={searchString}
              />
            </Form.Field>
          )}

          {!!selectedCampaignId && (
            <>
              <Form.Field label="Selected Campaign">
                <Campaign
                  callbacks={{
                    deselectCampaign: () => setState({ selectedCampaignId: undefined }),
                  }}
                  campaign={campaign}
                />
              </Form.Field>

              <Form.Field label="* Category" marginTop="large">
                <select
                  onChange={e => selectServiceCategoryId(Number(e.target.value))}
                  value={selectedServiceCategoryId}
                >
                  <option value="">Please Select...</option>
                  {filteredServiceCategories.map(serviceCategory => (
                    <option key={serviceCategory.id} value={serviceCategory.id}>
                      {serviceCategory.title}
                    </option>
                  ))}
                </select>
              </Form.Field>

              {!!selectedServiceCategoryId && (
                <Form.Field label="* Product" marginTop="large">
                  <select
                    onChange={e => selectServiceProductId(Number(e.target.value))}
                    value={selectedServiceProductId}
                  >
                    <option value="">Please Select...</option>
                    {filteredServiceProducts.map(serviceProduct => (
                      <option key={serviceProduct.id} value={serviceProduct.id}>
                        {serviceProduct.title}
                      </option>
                    ))}
                  </select>
                </Form.Field>
              )}
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creatingOrder}
          // @ts-expect-error TS2345
          onClick={() => createOrder(createOrderPayload)}
          size="large"
        >
          Create Order
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateCampaignPublicationOrderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
